import { Box, Stack, Typography, useTheme } from "@mui/material";
import { type FC, useRef } from "react";
import { ExpandToggleButton } from "web_myplann/Shared/Components/ExpandToggleButton";
import { useTextTruncation } from "web_myplann/Shared/Hooks/useTextTruncation";
import {
	getHtmlContentSx,
	isHtmlContent,
	sanitizeHtmlColors,
} from "web_myplann/Shared/Utils/htmlContentUtils";

const multiLineSx = {
	whiteSpace: "pre-line",
	overflowWrap: "break-word",
	display: "-webkit-box",
	WebkitBoxOrient: "vertical",
	overflow: "hidden",
	maxWidth: "100%",
} as const;

type Props = {
	label: string | null | undefined;
	text: string | null | undefined;
	maxLines?: number;
};

export const DataNote: FC<Props> = ({ label, text, maxLines = 4 }) => {
	const theme = useTheme();
	const textRef = useRef<HTMLDivElement>(null);
	const { isTruncated, isExpanded, toggleExpanded } = useTextTruncation(
		textRef,
		[text],
	);

	if (!text) {
		return null;
	}

	return (
		<Stack direction="column" alignItems="left" gap={0.5} py={0.4}>
			<Typography
				variant="body1"
				color={theme.palette.grey[500]}
				sx={{
					...multiLineSx,
				}}
			>
				{label}
			</Typography>

			{isHtmlContent(text) ? (
				<Box
					ref={textRef}
					sx={getHtmlContentSx({ theme, showMore: isExpanded, maxLines })}
					// biome-ignore lint/security/noDangerouslySetInnerHtml: HTML content from notes
					dangerouslySetInnerHTML={{
						__html: sanitizeHtmlColors(text, theme.palette.mode === "dark"),
					}}
				/>
			) : (
				<Typography
					ref={textRef}
					fontSize={14}
					fontWeight={400}
					color={theme.palette.grey[700]}
					sx={{
						...multiLineSx,
						WebkitLineClamp: isExpanded ? "unset" : maxLines,
					}}
				>
					{text}
				</Typography>
			)}

			{(isTruncated || isExpanded) && (
				<ExpandToggleButton
					isExpanded={isExpanded}
					onClick={toggleExpanded}
					fontSize={14}
				/>
			)}
		</Stack>
	);
};
